import React, {
  useState,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import { format } from "date-fns";
import { useInView } from "react-intersection-observer";
import { useParams } from "react-router-dom";
import {
  CaretCircleLeft,
  CaretCircleRight,
  CircleNotch,
  Sun,
  Cloud,
  DotsThreeOutlineVertical,
  Download,
  Info,
} from "phosphor-react";
import { getField } from "api/Field";
import useOnClickOutside from "hooks/useOnClickOutside";
import {
  useMappingCatalogQuery,
  useStackCatalogQuery,
  useZoningCatalogQuery,
} from "hooks/useMappingCatalog";
import { UserContext } from "context/user";
import { MapContext, MapActions } from "context/map";
import WarningModal from "components/Modal/Warning";
import SelectCropTypes from "components/Modal/SelectCropTypes";
import "./index.scss";

const Catalog = () => {
  const searchDays = 365;
  const { farmId } = useParams();
  const { ref, inView } = useInView();
  const catalogContainer = useRef(null);
  const zoningOptionRef = useRef(null);
  const stackOptionRef = useRef(null);
  const { token } = useContext(UserContext);
  const { state, dispatch } = useContext(MapContext);
  const {
    mode,
    tutorialMode,
    selectedFields,
    selectedLayer,
    catalog,
    selectedDate,
    selectedAlgorithm,
    exportMode,
    selectedZone,
    harvestMoistureData,
  } = state;
  const mappingCatalogQueryData = {
    selectedFields,
    selectedLayer,
    searchDays,
    farmId,
  };
  const {
    data: mappingCatalogData,
    refetch: catalogRefetch,
    isLoading: isMappingCatalogLoading,
    fetchNextPage,
    isFetchingNextPage,
  } = useMappingCatalogQuery(mappingCatalogQueryData);
  const zoningCatalogQueryData = { selectedLayer, selectedFields, farmId };
  const {
    data: zoningCatalogData,
    refetch: zoningCatalogRefetch,
    isLoading: isZoningCatalogLoading,
  } = useZoningCatalogQuery(zoningCatalogQueryData);
  const stackCatalogQueryData = {
    selectedLayer,
    selectedFields,
    farmId,
  };
  const {
    data: stackCatalogData,
    refetch: stackCatalogRefetch,
    isLoading: isStackCatalogLoading,
  } = useStackCatalogQuery(stackCatalogQueryData);
  const [isOpenWarningModal, setOpenWarningModal] = useState(false);
  const [isOpenCropTypeModal, setOpenCropTypeModal] = useState(false);
  const [openZoneOptions, setOpenZoneOptions] = useState(null);
  useOnClickOutside(zoningOptionRef, () => setOpenZoneOptions(null));
  const [openStackOptions, setOpenStackOptions] = useState(null);
  useOnClickOutside(stackOptionRef, () => setOpenStackOptions(null));

  useEffect(() => {
    const mapControllerContainer = document.getElementsByClassName(
      "mapboxgl-ctrl-bottom-right"
    );
    const mapCoordinatesContainer = document.getElementsByClassName(
      "mapboxgl-coordinates-bottom-right"
    );
    const measureControllerContainer =
      document.getElementsByClassName("measure-tools");

    const mapboxControlContainer = document.getElementsByClassName(
      "mapboxgl-ctrl-scale"
    );
    const mapboxGeolocateContainer = document.getElementsByClassName(
      "mapboxgl-ctrl-geolocate"
    );
    const catalogContainers =
      document.getElementsByClassName("timeline-wrapper");
    const mapboxResetContainer = document.getElementsByClassName("map-reset");
    const mapboxLogoContainer =
      document.getElementsByClassName("mapboxgl-logo");
    const legendContainer = document.getElementsByClassName("legend");

    if (!mapControllerContainer.length && !tutorialMode) return;
    if (!mapCoordinatesContainer.length) return;

    if (
      (selectedFields.length && mode === "view") ||
      tutorialMode ||
      selectedLayer === "zoning" ||
      selectedLayer === "stack"
    ) {
      mapControllerContainer[0].classList.add("-with-catalog");
      measureControllerContainer[0]?.classList.add("-with-catalog");
      mapCoordinatesContainer[0].classList.add("-with-catalog");
      mapboxControlContainer[0]?.classList.add("-with-catalog");
    } else {
      mapControllerContainer[0].classList.remove("-with-catalog");
      measureControllerContainer[0]?.classList.remove("-with-catalog");
      mapCoordinatesContainer[0].classList.remove("-with-catalog");
      mapboxControlContainer[0]?.classList.remove("-with-catalog");
    }
    if (exportMode) {
      mapboxControlContainer[0]?.classList.add("-for-export");
      mapControllerContainer[0]?.classList.add("-for-export");
      mapboxControlContainer[0]?.classList.add("-for-export");
      mapboxGeolocateContainer[0]?.classList.add("-for-export");
      mapboxResetContainer[0]?.classList.add("-for-export");
      mapboxLogoContainer[0]?.classList.add("-for-export");
      catalogContainers[0]?.classList.add("-for-export");
      legendContainer[0]?.classList.add("-for-export");
    } else {
      mapControllerContainer[0]?.classList.remove("-for-export");
      mapboxControlContainer[0]?.classList.remove("-for-export");
      mapboxGeolocateContainer[0]?.classList.remove("-for-export");
      mapboxResetContainer[0]?.classList.remove("-for-export");
      mapboxLogoContainer[0]?.classList.remove("-for-export");
      catalogContainers[0]?.classList.remove("-for-export");
      mapboxControlContainer[0]?.classList.remove("-for-export");
      legendContainer[0]?.classList.remove("-for-export");
    }
    if (mode === "zoning") {
      mapboxControlContainer[0]?.classList.remove("-with-catalog");
    }
  }, [mode, selectedFields, selectedLayer, tutorialMode, exportMode]);

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage]);

  const continueFetchCatalog = () => {
    setOpenWarningModal(false);
    catalogRefetch();
  };

  const setMappingCatalog = useCallback(() => {
    if (mappingCatalogData) {
      const selectedPage = mappingCatalogData.pages.length - 1;
      const { dates } =
        mappingCatalogData.pages[selectedPage].features[0].properties;
      const sortedDates = dates.sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );

      if (selectedPage > 0) {
        dispatch({
          type: MapActions.FETCH_MORE_CATALOG,
          value: sortedDates,
        });
      } else {
        dispatch({
          type: MapActions.SET_CATALOG,
          value: sortedDates,
        });
      }
    }
  }, [mappingCatalogData, dispatch]);

  useEffect(() => {
    setMappingCatalog();
  }, [setMappingCatalog]);

  const fetchCatalog = useCallback(() => {
    if (
      selectedLayer === "zoning" ||
      (selectedLayer === "zoning" && selectedFields.length)
    ) {
      zoningCatalogRefetch();
      return;
    }

    if (
      selectedLayer === "stack" ||
      (selectedFields.length && selectedLayer === "stack")
    ) {
      stackCatalogRefetch();
    }

    if (selectedFields.length > 50) {
      setOpenWarningModal(true);

      return;
    }

    if (
      (selectedFields.length && selectedLayer !== "stack") ||
      selectedLayer === "sensor_em"
    ) {
      catalogRefetch();
    }
  }, [
    catalogRefetch,
    selectedFields.length,
    selectedLayer,
    zoningCatalogRefetch,
    stackCatalogRefetch,
  ]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  const setZoningCatalog = useCallback(() => {
    dispatch({
      type: MapActions.SET_CATALOG,
      value: [],
    });

    if (zoningCatalogData) {
      const sortedDates = zoningCatalogData.data.sort(
        (a, b) => new Date(b.catalog_entry) - new Date(a.catalog_entry)
      );

      dispatch({
        type: MapActions.SET_CATALOG,
        value: sortedDates,
      });
    }
  }, [dispatch, zoningCatalogData]);

  useEffect(() => {
    setZoningCatalog();
  }, [setZoningCatalog]);

  const setStackCatalog = useCallback(() => {
    dispatch({
      type: MapActions.SET_CATALOG,
      value: [],
    });

    if (stackCatalogData) {
      const sortedDates = stackCatalogData.data.sort(
        (a, b) => new Date(b.dates) - new Date(a.dates)
      );

      dispatch({
        type: MapActions.SET_CATALOG,
        value: sortedDates,
      });
    }
  }, [dispatch, stackCatalogData]);

  useEffect(() => {
    setStackCatalog();
  }, [setStackCatalog]);

  const currentAlgorithmItem = (items) => {
    return items.find((item) => item.algorithm === selectedAlgorithm);
  };

  const unselectAllFields = () => {
    setOpenWarningModal(false);
    dispatch({
      type: MapActions.REMOVE_SELECTED_FIELDS,
    });
  };

  const handleSelectDate = async (date) => {
    if (selectedFields.length === 1 && selectedLayer === "standard") {
      const fieldInfo = await getField(selectedFields[0], token);

      if (fieldInfo && fieldInfo.properties.field_crop_type === "") {
        setOpenCropTypeModal(true);
      } else {
        dispatch({ type: MapActions.SET_SELECTED_DATE, value: date });
      }
    } else {
      dispatch({ type: MapActions.SET_SELECTED_DATE, value: date });
    }
  };

  const toggleZoneOption = (event, id) => {
    dispatch({
      type: MapActions.SET_SELECTED_ZONE,
      value: id,
    });
    setOpenZoneOptions(id);
    event.stopPropagation();
  };

  const toggleZoneEdit = (zoneCatalog) => {
    const zones = JSON.parse(zoneCatalog.zones);
    const zoneFeatures = selectedFields.length
      ? zones.features.filter((item) =>
          selectedFields.includes(item.properties.field_id.toString())
        )
      : zones.features;
    const data = {
      zonedFields: zoneCatalog.fields,
      numberOfZones: zoneCatalog.num_zones,
      zoningOrderId: zoneCatalog.id,
      rates: zones.rates,
      features: zoneFeatures,
    };
    dispatch({ type: MapActions.SET_ZONING_EDIT_MODE, value: data });
  };

  const toggleStackEdit = (stackCatalog) => {
    const zones = JSON.parse(stackCatalog.zones);
    const stackRates = { 1: "0", 2: "0", 3: "0", 4: "0", 5: "0" };
    const zoneFeatures = selectedFields.length
      ? zones.features.filter((item) =>
          selectedFields.includes(item.properties.field_id.toString())
        )
      : zones.features;
    const data = {
      zonedFields: stackCatalog.field_ids,
      numberOfZones: zones?.rates ? Object.keys(zones.rates).length : 5,
      zoningOrderId: stackCatalog.id,
      rates: zones.rates || stackRates,
      features: zoneFeatures,
    };
    dispatch({ type: MapActions.SET_ZONING_EDIT_MODE, value: data });
  };

  const toggleStackOption = (event, id) => {
    dispatch({
      type: MapActions.SET_SELECTED_ZONE,
      value: id,
    });
    setOpenStackOptions(id);
    event.stopPropagation();
  };

  const renderCatalogNavigation = () => {
    if (!catalog.length) return null;

    return (
      <>
        <button
          className="scroll-navigate"
          type="button"
          onClick={() => {
            catalogContainer.current.scrollLeft += -1000;
          }}
          disabled={isFetchingNextPage}
        >
          <CaretCircleLeft size={24} color="#fff" weight="duotone" />
        </button>
        <button
          className="scroll-navigate"
          type="button"
          onClick={() => {
            catalogContainer.current.scrollLeft += 1000;
          }}
        >
          <CaretCircleRight size={24} color="#fff" weight="duotone" />
        </button>
      </>
    );
  };

  const renderLoading = () => {
    return (
      <div className="loader">
        <CircleNotch size={20} color="#09ba51" /> Loading Available Dates
      </div>
    );
  };

  const renderClouds = (items) => {
    const cloudPercentage = currentAlgorithmItem(items).cloud_percent;
    const sunPercentage = cloudPercentage === 0 ? "" : `${cloudPercentage}%`;

    if (cloudPercentage < 10) {
      return (
        <small className="cloud">
          <Sun size={20} color="#fff" weight="duotone" />
          {sunPercentage}
        </small>
      );
    }

    return (
      <small className="cloud">
        <Cloud size={20} color="#fff" weight="duotone" />
        {cloudPercentage} %
      </small>
    );
  };

  const renderStandardDates = (item) => {
    const { date, items } = item;
    const catalogDate = new Date(date);
    const presentYear = format(new Date(), "yyyy");
    const dateYear = format(catalogDate, "yyyy");
    const displayDate =
      presentYear !== dateYear
        ? format(catalogDate, "d MMM, yy")
        : format(catalogDate, "d MMM");

    return (
      <>
        {displayDate}
        {renderClouds(items)}
      </>
    );
  };

  const renderEMDates = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    return (
      <>
        <span className="em-badge">EM</span>
        {format(startDate, "MMM d, yyyy")} to {format(endDate, "MMM d, yyyy")}
      </>
    );
  };

  const renderItem = (item) => {
    if (selectedLayer !== "sensor_em") {
      return renderStandardDates(item);
    }

    return renderEMDates(item.start_date, item.end_date);
  };

  const renderMappingCatalogItems = () => {
    if (!catalog.length) {
      return <div className="no-dates">No Dates Available</div>;
    }

    return catalog.map((item) => (
      <div
        key={item.date}
        className={`date ${
          selectedDate === item.date || selectedDate === item.start_date
            ? "-selected"
            : ""
        }`}
        onClick={() => {
          const date = item.date || item.start_date;

          handleSelectDate(date);
        }}
        aria-hidden
      >
        {renderItem(item)}
        <div ref={ref} className="hidden" />
      </div>
    ));
  };

  const renderMappingCatalog = () => {
    return (
      <div className="dates-wrapper" ref={catalogContainer}>
        {isMappingCatalogLoading
          ? renderLoading()
          : renderMappingCatalogItems()}
      </div>
    );
  };

  const zoningCatalogAlgorithms = (algorithm) => {
    const algorithmList = {
      standard_ndvi: "NDVI",
      "standard_ndvi-r": "NDVI_R",
      standard_ndre: "NDRE",
      standard_pcd: "PCD",
      standard_thermal: "THERMAL",
    };

    return algorithmList[algorithm];
  };

  const renderZoningCatalogItems = () => {
    if (!catalog.length) {
      return <div className="no-dates">No Dates Available</div>;
    }

    return catalog.map((item) => (
      <div
        key={item.id}
        id={`zone-${item.id}`}
        className={`date ${selectedZone === item.id ? "-selected" : ""}`}
        onClick={() => {
          dispatch({
            type: MapActions.SET_SELECTED_ZONE,
            value: item.id,
          });
        }}
        aria-hidden
      >
        <div className="zone">
          <div>{item.num_zones} Zones</div>
          <div className="separator" />
          <div>{zoningCatalogAlgorithms(item.algorithm)}</div>
          <div className="separator" />
          <div>{format(new Date(item.catalog_entry), "MMM d, yyyy")}</div>
          <button
            type="button"
            className="action"
            onClick={(event) => toggleZoneOption(event, item.id)}
          >
            <DotsThreeOutlineVertical size={20} color="white" weight="fill" />
          </button>
        </div>
      </div>
    ));
  };

  const renderZoningCatalog = () => {
    return (
      <div className="dates-wrapper" ref={catalogContainer}>
        {isZoningCatalogLoading ? renderLoading() : renderZoningCatalogItems()}
      </div>
    );
  };

  const renderZoningOptions = () => {
    const element = document.getElementById(`zone-${openZoneOptions}`);
    const { left } = element.getBoundingClientRect();
    const zoneCatalog = catalog.find((item) => item.id === openZoneOptions);

    return (
      <div
        className="zone-options"
        style={{ left: left + 65 }}
        ref={zoningOptionRef}
      >
        <div
          className="item -edit"
          onClick={() => toggleZoneEdit(zoneCatalog)}
          aria-hidden
        >
          Adjust Rates
        </div>
        <div className="label">Export as</div>
        <div className="items">
          <a
            href={`${process.env.REACT_APP_DATA_FARMING_API_ORIGIN}/v2/zoning/download/${zoneCatalog.download_key}?unified=false`}
            className="item"
          >
            <Download size={20} color="#6F7D68" className="icon" />
            Individual Fields ({zoneCatalog.fields.length}{" "}
            {zoneCatalog.fields.length > 1 ? "Files" : "File"})
          </a>
          <a
            href={`${process.env.REACT_APP_DATA_FARMING_API_ORIGIN}/v2/zoning/download/${zoneCatalog.download_key}?unified=true`}
            className="item"
          >
            <Download size={20} color="#6F7D68" className="icon" />
            Entire Farm (1 File)
          </a>
        </div>
      </div>
    );
  };

  const prepareStackDates = (years) => {
    const formattedYears = years?.map((year) => format(new Date(year), "yyyy"));
    return formattedYears
      .slice()
      .sort((p, q) => p - q)
      .reduce((acc, cur, idx, src) => {
        if (idx > 0 && cur - src[idx - 1] === 1) acc[acc.length - 1][1] = cur;
        else acc.push([cur]);
        return acc;
      }, [])
      .map((range) => range.join("-"));
  };

  const renderStackCatalogItems = () => {
    if (!catalog.length) {
      return <div className="no-dates">No Dates Available</div>;
    }

    return catalog.map((item) => (
      <div
        key={item.id}
        id={`stack-${item.id}`}
        className={`date ${selectedZone === item.id ? "-selected" : ""}`}
        onClick={() => {
          dispatch({
            type: MapActions.SET_SELECTED_ZONE,
            value: item.id,
          });
        }}
        aria-hidden
      >
        <div className="zone">
          <div>{item.dates?.length} Years</div>
          <div className="separator" />
          {item.dates?.length <= 3 ? (
            item.dates.map((date) => (
              <>
                <p>{format(new Date(date), "yyyy")}</p>
                <div className="separator" />
              </>
            ))
          ) : (
            <p>{prepareStackDates(item.dates)}</p>
          )}
          <button
            type="button"
            className="action"
            onClick={(event) => toggleStackOption(event, item.id)}
          >
            <DotsThreeOutlineVertical size={20} color="white" weight="fill" />
          </button>
        </div>
      </div>
    ));
  };

  const renderStackCatalog = () => {
    return (
      <div className="dates-wrapper" ref={catalogContainer}>
        {isStackCatalogLoading ? renderLoading() : renderStackCatalogItems()}
      </div>
    );
  };

  const renderStackOptions = () => {
    const element = document.getElementById(`stack-${openStackOptions}`);
    const { left } = element.getBoundingClientRect();
    const stackCatalog = catalog.find((item) => item.id === openStackOptions);

    return (
      <div
        className="stack-options"
        style={{ left: left + 65 }}
        ref={stackOptionRef}
      >
        <div
          className="item -edit"
          onClick={() => toggleStackEdit(stackCatalog)}
          aria-hidden
        >
          Adjust Rates
        </div>
        <div className="label">Export as</div>
        <div className="items">
          <a
            href={`${process.env.REACT_APP_DATA_FARMING_API_ORIGIN}/v2/stack/download/${stackCatalog.download_key}?unified=false`}
            className="item"
          >
            <Download size={20} color="#6F7D68" className="icon" />
            Individual Fields ({stackCatalog?.fields_ids?.length}{" "}
            {stackCatalog?.fields_ids?.length > 1 ? "Files" : "File"})
          </a>
          <a
            href={`${process.env.REACT_APP_DATA_FARMING_API_ORIGIN}/v2/stack/download/${stackCatalog.download_key}?unified=true`}
            className="item"
          >
            <Download size={20} color="#6F7D68" className="icon" />
            Entire Farm (1 File)
          </a>
        </div>
      </div>
    );
  };

  const renderHarvestMoistureCatalog = () => {
    return (
      <div className="harvest-moisture-wrapper">
        {harvestMoistureData && harvestMoistureData?.length ? (
          <div className="harvest-moisture-content">
            <Info size={20} color="#FFFFFF" weight="fill" />
            Harvest predictions will be updated with every new cloud-free image.
            <p className="description">
              If you have any feedback contact us at{" "}
              <a className="email" href="mailto:support@datafarming.com.au">
                support@datafarming.com.au
              </a>{" "}
            </p>
          </div>
        ) : (
          <div className="harvest-moisture-content">
            <Info size={20} color="#FFFFFF" weight="fill" />
            Our harvest forecasting process will begin when we detect your crop
            is in drydown. (This service is currently limited to Australia)
          </div>
        )}
      </div>
    );
  };

  if (isOpenWarningModal) {
    return (
      <WarningModal
        isOpen
        onClose={unselectAllFields}
        fetchCatalog={continueFetchCatalog}
        unselectAllFields={unselectAllFields}
      />
    );
  }

  const renderCatalog = () => {
    if (selectedLayer === "zoning") {
      return renderZoningCatalog();
    }
    if (selectedLayer === "stack") {
      return renderStackCatalog();
    }
    if (selectedLayer === "rice_harvest_prediction") {
      return renderHarvestMoistureCatalog();
    }
    return renderMappingCatalog();
  };

  return (
    <>
      <div
        className={`timeline-wrapper ${
          selectedFields.length ||
          tutorialMode ||
          selectedLayer === "zoning" ||
          selectedLayer === "stack" ||
          selectedLayer === "rice_harvest_prediction"
            ? ""
            : "-hidden"
        }`}
        data-tour="step-3"
      >
        {renderCatalogNavigation()}
        {renderCatalog()}
        <SelectCropTypes
          isOpen={isOpenCropTypeModal}
          onClose={() => setOpenCropTypeModal(!isOpenCropTypeModal)}
        />
      </div>
      {selectedLayer === "zoning" && openZoneOptions && renderZoningOptions()}
      {selectedLayer === "stack" && openStackOptions && renderStackOptions()}
    </>
  );
};

export default Catalog;
